import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  TextField,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getIconDirection } from "modules/getIconDirection";

import {
  fetchJunctionList,
  fetchRcmList,
  fetchJunctionData,
  fetchRcmData,
} from "./functions";

import config from "../../../config";

mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN_3D;

const INIT_VIEW = {
  center: [22.9527, 40.6253],
  zoom: 15,
  pitch: 63,
  bearing: 0,
};

/* ---------------- helpers for the icon layer ---------------- */

async function ensureDirectionImages(map) {
  const missing = [1, 2, 3, 4].filter((d) => !map.hasImage(`direction-${d}`));
  if (!missing.length) return;

  await Promise.all(
    missing.map(
      (code) =>
        new Promise((resolve, reject) => {
          map.loadImage(getIconDirection(code), (err, img) => {
            if (err) return reject(err);
            if (!map.hasImage(`direction-${code}`)) {
              map.addImage(`direction-${code}`, img);
            }
            resolve();
          });
        })
    )
  );
}

async function createIconLayer(map, layerConfig, data) {
  const { id, style = {}, interactions = {} } = layerConfig;

  await ensureDirectionImages(map);

  const enrichedData = {
    type: "FeatureCollection",
    features: (data?.features ?? []).map((f, i) => ({ ...f, id: f.id ?? i })),
  };

  if (map.getLayer(`${id}-icons`)) map.removeLayer(`${id}-icons`);
  if (map.getLayer(`${id}-labels`)) map.removeLayer(`${id}-labels`);
  if (map.getSource(id)) map.removeSource(id);

  map.addSource(id, { type: "geojson", data: enrichedData });

  const baseIconSize = style.layout?.["icon-size"] ?? 1.0;
  const baseTextSize = style.labelLayout?.["text-size"] ?? 18;
  const baseIconOpacity = style.paint?.["icon-opacity"] ?? 1.0;

  map.addLayer({
    id: `${id}-icons`,
    type: "symbol",
    source: id,
    layout: {
      "icon-image": [
        "concat",
        "direction-",
        ["to-string", ["coalesce", ["get", "direction"], 1]],
      ],
      "icon-size": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        baseIconSize * 0.9,
        ["boolean", ["feature-state", "selected"], false],
        baseIconSize * 1.1,
        baseIconSize,
      ],
      "icon-allow-overlap": true,
      ...(style.layout || {}),
    },
    paint: {
      "icon-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.6,
        ["boolean", ["feature-state", "selected"], false],
        0.85,
        baseIconOpacity,
      ],
      ...(style.paint || {}),
    },
  });

  map.addLayer({
    id: `${id}-labels`,
    type: "symbol",
    source: id,
    layout: {
      "text-field": ["coalesce", ["get", "text_field"], ""],
      "text-size": baseTextSize,
      "text-variable-anchor": ["top", "right"],
      "text-radial-offset": 0.6,
      "text-justify": "auto",
      "text-allow-overlap": false,
      ...(style.labelLayout || {}),
    },
    paint: {
      "text-color": "#ffffff",
      "text-halo-color": "#000000",
      "text-halo-width": 8,
      "text-halo-blur": 0,
      ...(style.labelPaint || {}),
    },
  });

  const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
  let hoveredId = null;
  let selectedId = null;
  let clickOpen = false;

  const setHover = (fid, on) =>
    map.setFeatureState({ source: id, id: fid }, { hover: on });
  const setSelected = (fid, on) =>
    map.setFeatureState({ source: id, id: fid }, { selected: on });

  const targets = [`${id}-icons`, `${id}-labels`];

  // if (interactions.hover) {
  //   targets.forEach((layer) => {
  //     map.on("mouseenter", layer, (e) => {
  //       console.log("item : ", e.features?.[0].properties);

  //       // if (clickOpen) return;
  //       // map.getCanvas().style.cursor = "pointer";
  //       // const f = e.features?.[0];
  //       // if (!f) return;

  //       // if (hoveredId !== null) setHover(hoveredId, false);
  //       // hoveredId = f.id;
  //       // setHover(hoveredId, true);

  //       // const html =
  //       //   f.properties?.displayMessages || f.properties?.displayMessage;
  //       // if (html) popup.setLngLat(e.lngLat).setHTML(html).addTo(map);
  //     });

  //     // map.on("mouseleave", layer, () => {
  //     //   if (!clickOpen) popup.remove();
  //     //   map.getCanvas().style.cursor = "";
  //     //   if (hoveredId !== null) {
  //     //     setHover(hoveredId, false);
  //     //     hoveredId = null;
  //     //   }
  //     // });
  //   });
  // }

  // if (interactions.popup) {
  //   targets.forEach((layer) => {
  //     map.on("click", layer, (e) => {
  //       clickOpen = true;
  //       const f = e.features?.[0];
  //       if (!f) return;

  //       if (selectedId !== null && selectedId !== f.id) {
  //         setSelected(selectedId, false);
  //       }
  //       selectedId = f.id;
  //       setSelected(selectedId, true);

  //       const html =
  //         f.properties?.displayMessages || f.properties?.displayMessage || "";
  //       popup.setLngLat(e.lngLat).setHTML(`<strong>${html}</strong>`).addTo(map);
  //     });
  //   });

  //   popup.on("close", () => {
  //     clickOpen = false;
  //     if (selectedId !== null) {
  //       setSelected(selectedId, false);
  //       selectedId = null;
  //     }
  //   });
  // }

  return {
    update(dataNext) {
      const src = map.getSource(id);
      if (!src) return;
      const next = {
        type: "FeatureCollection",
        features: (dataNext?.features ?? []).map((f, i) => ({
          ...f,
          id: f.id ?? i,
        })),
      };
      src.setData(next);
    },
    remove() {
      if (map.getLayer(`${id}-labels`)) map.removeLayer(`${id}-labels`);
      if (map.getLayer(`${id}-icons`)) map.removeLayer(`${id}-icons`);
      if (map.getSource(id)) map.removeSource(id);
    },
  };
}

/* ---------------- component ---------------- */

export default function IntersectionMonitoring() {
  const { t } = useTranslation();
  const [mode, setMode] = useState("junction");
  const [junctionList, setJunctionList] = useState([]);
  const [rcmList, setRcmList] = useState([]);
  const [junctionGeo, setJunctionGeo] = useState(null);
  const [rcmGeo, setRcmGeo] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedFlow, setSelectedFlow] = useState(null);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const iconLayerRef = useRef(null);

  const initializeSelection = (currentMode, jList, rList) => {
    if (currentMode === "junction" && jList.length > 0) return jList[0].id;
    if (currentMode === "rcm" && rList.length > 0) return rList[0].id;
    return "";
  };

  const getFirstFlowForJunction = (featureCollection, junctionId) => {
    if (!featureCollection?.features) return null;
    const flows = featureCollection.features
      .filter((f) => f?.properties?.junction === junctionId)
      .map((f) => f?.properties?.flow)
      .filter((v) => v !== null && v !== undefined)
      .sort((a, b) => a - b);
    return flows.length ? flows[0] : null;
  };

  useEffect(() => {
    const t = new Date();
    const timeString = t.toLocaleTimeString();
    console.log(`UseEffect Load List and data : ${timeString}`);
    
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const [jList, rList, jData, rData] = await Promise.all([
          fetchJunctionList(),
          fetchRcmList(),
          fetchJunctionData(),
          fetchRcmData(),
        ]);
        if (!isMounted) return;

        setJunctionList(jList);
        setRcmList(rList);
        setJunctionGeo(jData);
        setRcmGeo(rData);

        const initId = initializeSelection("junction", jList, rList);
        setSelectedItemId(initId);
        const firstFlow = getFirstFlowForJunction(jData, initId);
        if (firstFlow !== null) setSelectedFlow(firstFlow);

      } catch (err) {
        console.error("Error loading:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const t = new Date();
    const timeString = t.toLocaleTimeString();
    console.log(`useEffect Update data : ${timeString}`);

    
    let alive = true;

    async function refreshGeoData() {
      const t = new Date();
      const timeString = t.toLocaleTimeString();
      console.log(`refreshGeoData : ${timeString}`);

      try {
        const [jData, rData] = await Promise.all([
          fetchJunctionData(),
          fetchRcmData(),
        ]);
        if (!alive) return;
        setJunctionGeo(jData);
        setRcmGeo(rData);
      } catch (err) {
        console.error("Error refreshing geo data:", err);
      }
    }

    // First tick immediately
    refreshGeoData();

    const id = setInterval(refreshGeoData, 60 * 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const itemsToShow = useMemo(
    () => (mode === "junction" ? junctionList : rcmList),
    [mode, junctionList, rcmList]
  );

  const flowsForSelected = useMemo(() => {
    const fc = mode === "junction" ? junctionGeo : rcmGeo;
    if (!fc || !Array.isArray(fc.features) || !selectedItemId) return [];
    const flows = fc.features
      .filter((f) => f?.properties?.junction === selectedItemId)
      .map((f) => f?.properties?.flow)
      .filter((v) => v !== null && v !== undefined);
    return Array.from(new Set(flows)).sort((a, b) => a - b);
  }, [mode, junctionGeo, rcmGeo, selectedItemId]);

  useEffect(() => {
    if (flowsForSelected.length) {
      setSelectedFlow(flowsForSelected[0]);
    } else {
      setSelectedFlow(null);
    }
  }, [flowsForSelected]);

  useEffect(() => {
    setSelectedItemId(initializeSelection(mode, junctionList, rcmList));
  }, [mode, junctionList, rcmList]);

  const filteredGeoJson = useMemo(() => {
    const fc = mode === "junction" ? junctionGeo : rcmGeo;
    const empty = { type: "FeatureCollection", features: [] };
    if (!fc || !Array.isArray(fc.features) || !selectedItemId) return empty;

    let features = fc.features.filter(
      (f) => f?.properties?.junction === selectedItemId
    );
    if (selectedFlow !== null && selectedFlow !== undefined) {
      features = features.filter((f) => f?.properties?.flow === selectedFlow);
    }
    return { type: "FeatureCollection", features };
  }, [mode, junctionGeo, rcmGeo, selectedItemId, selectedFlow]);

  // 🔹 legend info (from first feature)
  const legendInfo = useMemo(() => {
    const first = filteredGeoJson?.features?.[0]?.properties;
    if (!first) return null;
    return {
      name: first.name,
      volume: first.metric_value_volumePerHour,
      timestamp: first.timestamp,
    };
  }, [filteredGeoJson]);

  // mount map
  useEffect(() => {
    if (loading) return;
    if (!containerRef.current) return;
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: config.MAP_3D,
      center: INIT_VIEW.center,
      zoom: INIT_VIEW.zoom,
      pitch: INIT_VIEW.pitch,
      bearing: INIT_VIEW.bearing,
    });
    mapRef.current = map;

    const onResize = () => map.resize();
    window.addEventListener("resize", onResize);

    map.on("load", async () => {
      iconLayerRef.current = await createIconLayer(
        map,
        {
          id: "flows",
          style: {
            layout: { "icon-size": 1.0 },
            labelLayout: { "text-size": 18 },
          },
          interactions: { hover: true, popup: true },
        },
        filteredGeoJson
      );
    });

    return () => {
      window.removeEventListener("resize", onResize);
      if (iconLayerRef.current) {
        iconLayerRef.current.remove();
        iconLayerRef.current = null;
      }
      map.remove();
      mapRef.current = null;
    };
  }, [loading]);

  // update icon layer when data changes
  useEffect(() => {
    if (!mapRef.current) return;
    if (!iconLayerRef.current) return;
    iconLayerRef.current.update(filteredGeoJson);
  }, [filteredGeoJson]);

  // fly to first feature for selected flow
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const feat = filteredGeoJson.features?.[0];
    if (!feat) return;

    const vs = feat.properties?.viewState;
    if (vs?.center && vs?.zoom) {
      map.flyTo({
        center: vs.center,
        zoom: vs.zoom,
        pitch: vs.pitch ?? 70,
        bearing: vs.bearing ?? 0,
        speed: 1.4,
        curve: 1.4,
        essential: true,
      });
    } else if (feat.geometry?.type === "Point") {
      const [lng, lat] = feat.geometry.coordinates;
      map.flyTo({
        center: [Number(lng), Number(lat)],
        zoom: 18,
        pitch: 70,
        bearing: 0,
        speed: 1.4,
        curve: 1.4,
        essential: true,
      });
    }
  }, [filteredGeoJson]);

  const handleModeChange = (e) => setMode(e.target.value);

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

return (
  <DashboardLayout>
    <Card sx={{ mt: 2, position: "relative", height: "85vh" }}>
      {/* Legend */}
      {legendInfo && (
        <MDBox
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            backgroundColor: "rgba(32,41,64,0.9)",
            color: "#fff",
            p: 2,
            borderRadius: 1,
            zIndex: 10,
            maxWidth: 250,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <MDTypography variant="body1">{legendInfo.name}</MDTypography>
          <MDTypography variant="body2">{legendInfo.volume}</MDTypography>
          <MDTypography variant="body2">{legendInfo.timestamp}</MDTypography>
        </MDBox>
      )}

      {/* Controls */}
      <MDBox
        sx={{
          position: "absolute",
          top: 24,
          right: 24,
          width: { xs: "90%", sm: 380 },
          bgcolor: "rgba(32,41,64,0.9)",
          px: 3.5,
          py: 3,
          borderRadius: 2,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem", // κενό ανάμεσα στα πεδία
        }}
      >
        {/* Data Type */}
        <TextField
          select
          fullWidth
          variant="outlined"
          label={t("trafficMonitoring.intersection.dataType")}
          value={mode}
          onChange={handleModeChange}
          InputLabelProps={{
            sx: {
              color: "#fff",
              "&.Mui-focused": { color: "#fff" },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              fontSize: "1rem",
              lineHeight: 2,
              fontWeight: 100,
              "& .MuiSelect-select": {
                py: 1.2, // ύψος πεδίου
              },
              "& fieldset": {
                borderColor: "#fff",
              },
              "&:hover fieldset": {
                borderColor: "#fff",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#fff",
              },
            },
          }}
        >
          <MenuItem value="junction">{t("trafficMonitoring.intersection.junctions")}</MenuItem>
          <MenuItem value="rcm">RCM</MenuItem>
        </TextField>

        {/* Select Junction / RCM */}
        <TextField
          select
          fullWidth
          variant="outlined"
          label={mode === "junction" ? t("trafficMonitoring.intersection.selectJunction") : t("trafficMonitoring.intersection.selectRcm")}
          value={selectedItemId}
          onChange={(e) => setSelectedItemId(e.target.value)}
          InputLabelProps={{
            sx: {
              color: "#fff",
              "&.Mui-focused": { color: "#fff" },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              fontSize: "1rem",
              lineHeight: 2,
              fontWeight: 100,
              "& .MuiSelect-select": {
                py: 1.2,
              },
              "& fieldset": {
                borderColor: "#fff",
              },
              "&:hover fieldset": {
                borderColor: "#fff",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#fff",
              },
            },
          }}
        >
          {itemsToShow.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name_en}
            </MenuItem>
          ))}
        </TextField>

        {/* Select Flow */}
        {flowsForSelected.length > 0 && (
          <TextField
            select
            fullWidth
            variant="outlined"
            label={t("trafficMonitoring.intersection.selectApproach")}
            value={selectedFlow ?? ""}
            onChange={(e) => setSelectedFlow(Number(e.target.value))}
            InputLabelProps={{
              sx: {
                color: "#fff",
                "&.Mui-focused": { color: "#fff" },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                fontSize: "1rem",
                lineHeight: 2,
                fontWeight: 100,
                "& .MuiSelect-select": {
                  py: 1.2,
                },
                "& fieldset": {
                  borderColor: "#fff",
                },
                "&:hover fieldset": {
                  borderColor: "#fff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#fff",
                },
              },
            }}
          >
            {flowsForSelected.map((flow) => (
              <MenuItem key={flow} value={flow}>
                {t("trafficMonitoring.intersection.approachLabel", { flow })}
              </MenuItem>
            ))}
          </TextField>
        )}
      </MDBox>

      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </Card>
  </DashboardLayout>
);

}
